import React, {useCallback, useEffect} from 'react';
import {FlatList, StyleSheet, View} from 'react-native';
import {useFocusEffect, useNavigationState} from '@react-navigation/native';
import {observer} from 'mobx-react';

import LoadingImg from '../../components/LoadingImg';
import StoreItem from '../../components/StoreItem';
import Button from '../../components/ui-controls/Button';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {AppRoute} from '../../models/AppRoute';
import {Shop} from '../../models/middleware';
import {RootStoreSelectionNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName, EventScreenName} from '../../utils/analytics/events';
import {navigateWhenReady} from '../../utils/navigation';

const StoreSelectionScreen = ({
  navigation,
}: RootStoreSelectionNavigatorScreenProps<AppRoute.MY_STORE_STORE_SELECTION>) => {
  const {shopSelectionStore} = useStores();
  const routeNames = useNavigationState(state => state.routeNames);
  const isInMainStack = routeNames.includes(AppRoute.HOME);

  const navigateToSuburbSelection = useCallback(() => {
    if (isInMainStack) {
      // This screen is registered in both RootStoreSelectionFlow (onboarding) and
      // MAIN stack (store switch). The prop type is RootStoreSelectionFlowParamList
      // which doesn't include HOME, but the MAIN stack navigator does.
      (
        navigation as unknown as {
          navigate: (route: string, params?: Record<string, unknown>) => void;
        }
      ).navigate(AppRoute.HOME, {openSuburbModal: true});
    } else {
      navigation.navigate(AppRoute.MY_STORE_SUBURB_SELECTION);
    }
  }, [isInMainStack, navigation]);

  // Mount-only guard: redirect to suburb selection if no suburbs loaded.
  // Intentionally runs once — not reactive to suburbList changes.
  useEffect(() => {
    if (shopSelectionStore.suburbList.length === 0) {
      navigateToSuburbSelection();
    }
  }, [navigateToSuburbSelection, shopSelectionStore]);

  // DIS-1914: an offer screen with no store sent the customer here. Leaving
  // the list for anything but the suburb picker on Home abandons that journey,
  // so its return route must not reach a later, unrelated store pick.
  useFocusEffect(
    useCallback(
      () => () => {
        // The MAIN instance of this screen shares its navigator with HOME; the
        // param-list type only knows the store-flow instance.
        const state = navigation.getState();
        const focusedRouteName: string | undefined = state?.routes[state.index]?.name;
        if (focusedRouteName !== AppRoute.HOME) {
          shopSelectionStore.consumePostSelectionReturnRoute();
        }
      },
      [navigation, shopSelectionStore],
    ),
  );

  // This is the completion point of store selection. Without a return route,
  // App.tsx swaps in ROOT once a store is set and the customer lands on Home.
  // SHORTCUT: the return route is set only from S2b, which customers reach only
  // in the case named at shopSelectionStore.postSelectionReturnRoute; the
  // delayedNavigationPath replay serves the rest. Upgrade path: remove the
  // consume here and the blur cleanup above if the replay covers every case.
  const selectStore = async (store: Shop) => {
    // Consume before selectStore: it remounts the navigation container, which
    // unmounts this screen and runs the cleanup above.
    const returnRoute = shopSelectionStore.consumePostSelectionReturnRoute();
    const selection = shopSelectionStore.selectStore(store);
    shopSelectionStore.setCanDeliver(store.delivery_available);
    shopSelectionStore.setCanPickup(store.pickup_available);

    Analytics.logEvent({
      eventName: EventName.StoreSelected,
      parameters: {
        screen_name: EventScreenName.StoreSelection,
        store_id: store.store_number,
        store_name: store.name,
        store_postcode: store.address?.postal_code,
        store_distance: store.distance,
        store_delivery_available: store.delivery_available,
        store_pickup_available: store.pickup_available,
      },
    });

    // After selectStore resolves, the store data and member data are loaded,
    // so the offer screen mounts against the new store.
    await selection;
    if (returnRoute) {
      navigateWhenReady(...returnRoute);
    }
  };

  const expandSearch = () => {
    shopSelectionStore.expandSearchRange();
    shopSelectionStore.resetLoading();
    shopSelectionStore.selectSuburb();
  };

  const handleChangeLocationPress = () => navigateToSuburbSelection();

  const separator = () => {
    return (
      <View
        style={{
          marginVertical: 4,
          borderBottomWidth: 1,
          borderBottomColor: 'lightgrey',
        }}
      />
    );
  };

  const date = new Date();
  const dayName = date.toLocaleString('au-AU', {weekday: 'long'}).toLowerCase();

  const ChangeLocationButton = () => {
    const suburbName = shopSelectionStore.selectedSuburb?.locality;
    return (
      <View>
        <Button
          onPress={handleChangeLocationPress}
          title="CHANGE LOCATION"
          testID="change-location-button"
        />
        <Text style={styles.changeLocationCopy}>Delivery options are shown for {suburbName}.</Text>
      </View>
    );
  };

  const storeList = () => {
    if (!shopSelectionStore.storeList.length) {
      return (
        <View style={styles.noStoresSection}>
          <Text>No stores were found within {shopSelectionStore.maxDistance} km of location.</Text>

          <View style={styles.searchAgainSection}>
            <Button
              onPress={expandSearch}
              title={`Search again up to ${shopSelectionStore.maxDistance + 200} km`}
            />
          </View>
        </View>
      );
    }
    return (
      <FlatList
        data={shopSelectionStore.storeList}
        renderItem={({item}) => (
          <StoreItem item={item} dayName={dayName} selectStore={selectStore} />
        )}
        ItemSeparatorComponent={separator}
        keyExtractor={item => item.store_number}
        fadingEdgeLength={100}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={ChangeLocationButton}
        ListHeaderComponentStyle={{marginBottom: 16}}
      />
    );
  };

  if (shopSelectionStore.isStoreListLoading()) {
    return (
      <View style={styles.primaryWaitingContainer}>
        <LoadingImg />
      </View>
    );
  }

  return (
    <ContentView testID="store-selection-screen">
      <View style={styles.container}>{storeList()}</View>
    </ContentView>
  );
};

const styles = StyleSheet.create({
  primaryWaitingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colour.background.general.primary,
  },
  container: {
    flex: 1,
  },
  noStoresSection: {
    marginTop: 30,
    textAlign: 'center',
  },
  searchAgainSection: {
    marginTop: 14,
    display: 'flex',
    justifyContent: 'center',
  },
  changeLocationCopy: {
    paddingTop: 8,
  },
});

export default observer(StoreSelectionScreen);
